
import './style.scss';
import { DataGrid as MuiDataGrid } from '@mui/x-data-grid';
import { useAuthContext } from '../../contexts/AuthContext';
import { useLanguageContext } from '../../contexts/LanguageContext';
import Pagination from './_Pagination';
import EmptyRows from './_EmptyRows';
import Filter from './_Filter';
import { useEffect, useCallback } from 'react';
import { RegistrationStatusEnum, RegistrationStatusData } from '../../data/Enums';
import DefaultConstants from '../../data/Constants';
import { GridColumns, DataGridProps } from "@mui/x-data-grid";
import StringHelper from '../../helpers/StringHelper';
import useState from '../../hooks/useState';


interface IDataGridProps extends Partial<DataGridProps> {
    columns: GridColumns;
    data: any[];
    rowsPerPage?: number | undefined;
    checkboxSelection?: boolean;
    openSearch?: boolean;
    hasPagination?: boolean;
    handleCloseSearch?: () => void;
}

interface IStateValues {
    search: string;
    rows: any[];
}


const DataGrid: React.FunctionComponent<IDataGridProps> = ({
    columns,
    data,
    rowsPerPage = undefined,
    openSearch = false,
    hasPagination = true,
    autoHeight = false,
    handleCloseSearch = () => {},
    onCellDoubleClick = () => {},
    getCellClassName = undefined,
    getRowClassName = undefined,
    checkboxSelection = false,
    disableSelectionOnClick = undefined,
    isRowSelectable = undefined,
    onSelectionModelChange = undefined
}) => {
    const { translate } = useLanguageContext();
    const { user } = useAuthContext();

    const state = useState<IStateValues>({
        search: '',
        rows: []
    });

    useEffect(() => {
        state.update('rows', data);
        handleSearch(state.values.search);
        // eslint-disable-next-line
    }, [data]);

    const handleSearch = useCallback((value: string) => {
        state.update('search', value);

        const searchRegex = new RegExp(StringHelper.Normalize(value), 'i');
        let filteredRows = data;

        if (value.trim().length > 0) {
            filteredRows = data.filter((row: any) => {
                return columns.some((column: any) => {
                    let rowColumn = row[column.field];
    
                    if (column.fieldKey) {
                        rowColumn = rowColumn[column.fieldKey];
                    }
                    
                    if (rowColumn) {
                        let rowValue = rowColumn.toString();
    
                        if (column.type === DefaultConstants.dataGridRegistrationStatusType) {
                            rowValue = translate(RegistrationStatusData[rowValue as RegistrationStatusEnum].name);
                        }
                        
                        return searchRegex.test(rowValue);
                    }
    
                    return false;
                });
            });
        }

        state.update('rows', filteredRows);
        // eslint-disable-next-line
    }, [data, columns]);


    return (
        <>
            <MuiDataGrid
                columns={columns}
                rows={state.values.rows}
                pageSize={rowsPerPage ?? user?.rowsPerPage ?? 20}
                pagination={hasPagination ? true : undefined}
                hideFooter={!hasPagination}
                hideFooterPagination={!hasPagination}
                components={{
                    NoRowsOverlay: EmptyRows,
                    Pagination: hasPagination ? Pagination : undefined,
                    Toolbar: Filter,
                }}
                componentsProps={{
                    toolbar: {
                        open: openSearch,
                        onClose: () => {
                            handleSearch('');
                            handleCloseSearch();
                        },
                        value: state.values.search,
                        onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleSearch(event.target.value)
                    },
                }}
                checkboxSelection={checkboxSelection}
                disableSelectionOnClick={disableSelectionOnClick}
                disableColumnMenu={true}
                className={`datagrid-default`}
                onCellDoubleClick={onCellDoubleClick}
                autoHeight={autoHeight}
                getCellClassName={getCellClassName}
                getRowClassName={getRowClassName}
                isRowSelectable={isRowSelectable}
                onSelectionModelChange={onSelectionModelChange}
            />
        </>
    );
}

export default DataGrid;